import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, Clock, Palmtree, UserX, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const TABS = [
    { key: "in", label: "Clocked in", icon: LogIn, tone: "text-emerald-600 bg-emerald-50" },
    { key: "late", label: "Late", icon: Clock, tone: "text-amber-600 bg-amber-50" },
    { key: "leave", label: "On leave", icon: Palmtree, tone: "text-klein bg-klein-50" },
    { key: "out", label: "Not in yet", icon: UserX, tone: "text-ink/60 bg-ink/5" },
];

function Row({ r, tab }) {
    return (
        <Link to={`/employees/${r.id}`} className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-ink/5" data-testid={`whos-in-row-${r.id}`}>
            <div className="w-8 h-8 rounded-full bg-ink/5 flex items-center justify-center text-[11px] font-bold text-ink/60 shrink-0">
                {r.name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm text-ink truncate">{r.name}</div>
                <div className="text-xs text-ink/50 truncate">{r.department || "No department"}{r.worksite ? ` · ${r.worksite}` : ""}</div>
            </div>
            <div className="text-xs font-semibold text-ink/60 tabular-nums whitespace-nowrap">
                {tab === "leave" ? (r.leave_type || "Leave") : r.check_in ? `${r.check_in}${r.checked_out ? " · out" : ""}` : ""}
            </div>
        </Link>
    );
}

// Live view of today's attendance; refreshes every minute.
export const WhosInPanel = () => {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("in");

    const load = useCallback(() => {
        api.get("/dashboard/whos-in").then(({ data: d }) => setData(d)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [load]);

    if (!data) return null;
    const rows = data[tab] || [];
    return (
        <div className="bg-white border border-ink/10 p-6 sm:p-8" data-testid="whos-in-panel">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="overline">Live · today</div>
                    <h2 className="mt-1 font-display font-bold text-2xl text-ink">Who&apos;s in today</h2>
                    <p className="mt-1 text-xs text-ink/50">{data.department ? `${data.department} · ` : ""}Late after {data.late_after}</p>
                </div>
                <button onClick={load} className="p-2 rounded-lg text-ink/50 hover:text-ink hover:bg-ink/5" title="Refresh" data-testid="whos-in-refresh">
                    <RefreshCw className="w-4 h-4" />
                </button>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`whos-in-tab-${t.key}`}
                        className={`text-left rounded-xl border p-3 transition-colors ${tab === t.key ? "border-klein bg-klein-50/50 dark:bg-klein/20" : "border-ink/10 hover:bg-ink/5"}`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.tone}`}><t.icon className="w-4 h-4" /></div>
                        <div className="mt-2 font-display font-extrabold text-2xl text-ink tabular-nums" data-testid={`whos-in-count-${t.key}`}>{data.counts[t.key]}</div>
                        <div className="text-xs text-ink/60">{t.label}</div>
                    </button>
                ))}
            </div>
            <div className="mt-4 max-h-72 overflow-y-auto" data-testid="whos-in-list">
                {rows.length ? rows.map((r) => <Row key={r.id} r={r} tab={tab} />) : (
                    <div className="py-8 text-center text-sm text-ink/40" data-testid="whos-in-empty">Nobody here right now.</div>
                )}
            </div>
        </div>
    );
};

export default WhosInPanel;
